import $ from "jquery";
import {BaseResponseObject} from "../Api/Types";
import Deferred = JQuery.Deferred;

type getter_info_t<T extends BaseResponseObject> = {
    items: T[],
    total_items: number
}

const DEFAULT_ITEMS_PER_PAGE = 10;

export class NavigableTable<RowT extends BaseResponseObject> {
    private readonly items_per_page_options = [5, 10, 15, 20, 25, 30, 40, 50];

    private readonly next_button: JQuery<HTMLButtonElement>;
    private readonly back_button: JQuery<HTMLButtonElement>;
    private readonly select_items_per_page: JQuery<HTMLSelectElement>;
    private readonly current_page: JQuery;
    private readonly tbody: JQuery;
    private readonly button_wrapper: JQuery;
    private items_per_page: number;
    private _pages: number;

    private _page: number = 1;
    private _cache: Record<number, getter_info_t<RowT>> = {};
    private readonly getter: (page_index: number, items_per_page: number) => Deferred<getter_info_t<RowT>>;
    private tune_to_fit_in: JQuery;

    public get effective_items_per_page () {
        return this.items_per_page ?? DEFAULT_ITEMS_PER_PAGE;
    }

    public constructor(
        table_wrapped: JQuery,
        private readonly row_builder: (item: RowT) => JQuery<HTMLTableRowElement>,
        getter: (page_index: number, items_per_page: number) => JQuery.Deferred<getter_info_t<RowT>>,
        items_per_page_or_tune_to_fit_in: JQuery|number = $("#page-container > :first-child"),
        private readonly cache_size: number = 5
    ) {
        const items_per_page = Number(items_per_page_or_tune_to_fit_in) === items_per_page_or_tune_to_fit_in ? items_per_page_or_tune_to_fit_in : undefined;
        const tune_to_fit_in = items_per_page === undefined ? <JQuery>items_per_page_or_tune_to_fit_in : undefined;

        this.items_per_page = items_per_page;
        this.tune_to_fit_in = tune_to_fit_in;
        this.tbody = table_wrapped.find("tbody");
        this.next_button = table_wrapped.find<HTMLButtonElement>(".next-btn");
        this.back_button = table_wrapped.find<HTMLButtonElement>(".previous-btn");
        this.current_page = table_wrapped.find<HTMLButtonElement>("[data-content-type='pagenNumber']");
        this.button_wrapper = table_wrapped.find<HTMLButtonElement>("[data-content-type='button_wrapper']");

        this.select_items_per_page = table_wrapped.find<HTMLSelectElement>('[data-content-type="itemsPerPage"]');

        this.next_button.on('click', () => {
            this.page++
        });

        this.back_button.on('click', () => {
            this.page--
        });

        this.getter = (page_index, items_per_page) => {
            if (page_index in this._cache)
                return $.Deferred().resolve(this._cache[page_index]);
            return getter(page_index, items_per_page).done(r => {
                const keys = Object.keys(this._cache);
                if (keys.length > this.cache_size)
                    delete this._cache[Number(keys[0])];

                this._cache[page_index] = r;
            });
        }

        this.init_select();
    }

    private init_select() {
        this.select_items_per_page.off("change");

        const idx = this.items_per_page_options.indexOf(this.effective_items_per_page);

        this.select_items_per_page.empty().append(...this.items_per_page_options.map(i => $("<option>").val(i).text(i)));
        this.select_items_per_page[0].selectedIndex = idx;

        this.select_items_per_page.on("change", () => {
            this.items_per_page = Number(this.select_items_per_page.val());
            this.drop_cache();
            this.update();
        })

    }

    public get page_index() {
        return this._page - 1;
    }

    public get page(): number {
        return this._page;
    }

    public get pages(): number {
        return this._pages;
    }

    public set page(page: number) {
        if (page > this.pages)
            page = this._pages - 1;
        else if (page < 1)
            page = 1;

        this._page = page;

        this.current_page.text(page + '/' + this.pages);

        this.update();
    }

    private set pages(pages: number) {
        this._pages = pages;
        if (this._pages !== void 0 && this._pages !== pages) {
            this.drop_cache();
            this.update();
            return
        }

        this.current_page.text(this.page + '/' + Math.max(1, pages));
        if (this.pages <= 1) {
            this.back_button.hide();
            this.next_button.hide();
            this.button_wrapper.removeClass('navigator').addClass('page-display');
        } else {
            this.back_button.show();
            this.next_button.show();
            this.button_wrapper.removeClass('page-display').addClass('navigator');
        }
    }

    public update() {
        this.next_button.add(this.back_button).prop("disabled", true);

        this.getter(this.page_index, this.effective_items_per_page).done(x => {
            if (this.items_per_page === undefined && x.items.length) {
                const row = this.row_builder(x.items[0]);
                this.tbody.empty().append(row);
                let c = 0;
                let m = Math.max(...this.items_per_page_options);
                while (this.tune_to_fit_in.prop("scrollHeight") <= this.tune_to_fit_in.prop("clientHeight")) {
                    this.tbody.append(row.clone(false));
                    c++;
                    if (c === m)
                        break;
                }
                let idx = this.items_per_page_options.findIndex(x => x > c);
                if (idx === -1)
                    idx = this.items_per_page_options.length - 1
                else if (idx !== 0)
                    idx--;

                const v = this.effective_items_per_page;
                this.items_per_page = this.items_per_page_options[idx];
                this.tbody.empty();

                this.init_select();

                if (v !== this.items_per_page) {
                    this.drop_cache();
                    this.update();
                    return;
                }
            }

            this.pages = Math.ceil(x.total_items / this.effective_items_per_page);

            this.tbody.empty().append(...x.items.map(i => this.row_builder(i)));

        }).always(() => {
            this.back_button.prop("disabled", this.page === 1);
            this.next_button.prop("disabled", this.page === this.pages);
        })
    }

    public drop_cache() {
        this._cache = {}
    }

    public drop_from_current() {
        for (const k of Object.keys(this._cache).map(Number)) {
            if (k >= this.page_index) {
                delete this._cache[k];
            }
        }
    }

    public drop_current() { // Chiamare su modifica
        delete this._cache[this.page_index];
    }

    public drop_greatest() { // Chiamare quando si aggiunge un record
        delete this._cache[Math.max(...Object.keys(this._cache).map(Number))];
    }
}
